import { loadBaseCSS } from '../shared/button-utils.js';
import { generateControlId, isAttrTruthy, reflectBoolean, upgradeProperty } from '../shared/form-control.js';
import { AnchoredOverlayController, overlayActiveElement, overlayContains, type OverlayPlacement } from '../shared/overlay-utils.js';
import { primitiveEvent, primitiveStyles, rovingIndex } from '../shared/primitive-utils.js';

export interface MenuItem { id: string; label: string; disabled?: boolean; type?: 'item'; }
export type MenuEntry = MenuItem | { type: 'separator' } | { type: 'group'; label: string; items: readonly MenuItem[] };

/**
 * Native action menu with ordinary and context triggers, groups and separators.
 * @element qds-menu
 * @attr {string} label - Visible trigger label and default menu name; default Actions.
 * @attr {string} menu-label - Optional distinct accessible menu name.
 * @attr {string} for - External native trigger ID; default internal button remains when unresolved.
 * @attr {string} context-for - Optional context surface ID; the ordinary trigger remains available.
 * @attr {boolean} open - Menu visibility; opening focuses an item.
 * @attr {boolean} controlled - Emit requests; application sets open.
 * @attr {boolean} disabled - Disable the built-in trigger and prevent opening.
 * @attr {'bottom-start'|'bottom-end'|'top-start'|'top-end'} placement - Preferred alignment; default bottom-start.
 * @property {ReadonlyArray<{id: string; label: string; disabled?: boolean; type?: 'item'} | {type: 'separator'} | {type: 'group'; label: string; items: ReadonlyArray<{id: string; label: string; disabled?: boolean}>}>} items - Item/group/separator data; first unique nonempty item ID wins.
 * @property {HTMLElement|null} anchor - Native external menu button; takes precedence over for.
 * @property {HTMLElement|null} contextTarget - Optional context surface, supporting right-click and Shift+F10/ContextMenu.
 * @csspart trigger - Built-in native menu button.
 * @csspart menu - Top-layer menu surface.
 * @csspart item - Native menu-item buttons.
 * @csspart group - Named action group.
 * @csspart separator - Noninteractive separator.
 * @fires {{component: HTMLElement; open: boolean; reason: string}} qds-open-change - Cancelable requested visibility.
 * @fires {{component: HTMLElement; open: boolean}} qds-toggle - Actual visibility changed.
 * @fires {{component: HTMLElement; id: string; item: {id: string; label: string; disabled?: boolean}}} qds-select - Cancelable item selection; preventing default keeps the menu open.
 */
export class QdsMenu extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'menu-label', 'for', 'context-for', 'open', 'controlled', 'disabled', 'placement']; }
  private _items: MenuEntry[] = [];
  private _flatItems: MenuItem[] = [];
  private _anchor: HTMLElement | null = null;
  private _contextTarget: HTMLElement | null = null;
  private _boundAnchor: HTMLElement | null = null;
  private _boundContext: HTMLElement | null = null;
  private _savedAttributes = new Map<string, string | null>();
  private _savedControls: Element[] | null = null;
  private _openAnchor: HTMLElement | null = null;
  private _point: { left: number; right: number; top: number; bottom: number; width: number; height: number } | null = null;
  private _trigger: HTMLButtonElement;
  private _panel: HTMLDivElement;
  private _controller: AnchoredOverlayController;
  private _visible = false;
  private _reflecting = false;
  private _upgrading = false;
  private _restoreOnClose = true;
  private _edge: 'first' | 'last' = 'first';
  private _search = '';
  private _searchedAt = 0;
  constructor() {
    super(); this.attachShadow({ mode: 'open' }); loadBaseCSS();
    this.shadowRoot!.innerHTML = `<style>${primitiveStyles}
      :host { display: inline-block; max-width: 100%; }
      [part=trigger], [part=item] { min-width: 44px; min-height: 44px; padding: .5rem .875rem; font: inherit; cursor: pointer; }
      [part=trigger] { border: 1px solid var(--theme-border-secondary, #a0aabe); border-radius: var(--theme-radius-sm, 4px); background: var(--theme-background-primary, #fff); color: var(--theme-text-primary, #333); }
      [part=trigger]:disabled { opacity: .55; cursor: not-allowed; }
      [part=menu] { box-sizing: border-box; position: fixed; inset: auto; margin: 0; padding: .25rem; width: max-content; min-width: 12rem;
        max-width: calc(100vw - 1rem); overflow: auto; overscroll-behavior: contain; border: 1px solid var(--theme-border-primary, #e0e0e0);
        border-radius: var(--theme-radius-sm, 4px); background: var(--theme-background-primary, #fff); color: var(--theme-text-primary, #333);
        box-shadow: var(--shadow-dropdown, 0 4px 12px rgba(0,0,0,.15)); z-index: var(--qds-popover-z, 1000); }
      [part=item] { display: block; width: 100%; max-width: 24rem; border: 0; border-radius: .125rem; background: transparent; text-align: start; color: inherit; overflow-wrap: anywhere; }
      [part=item]:hover, [part=item]:focus { background: var(--theme-background-secondary, #f8f8f8); }
      [part=item]:focus, [part=trigger]:focus, [part=menu]:focus { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: -2px; }
      [part=item][aria-disabled=true] { color: var(--theme-text-secondary, #555); cursor: not-allowed; }
      .group-label { padding: .5rem .875rem .25rem; font-weight: 600; }
      [part=separator] { border-block-start: 1px solid var(--theme-border-primary, #e0e0e0); margin: .25rem; }
      @media (forced-colors: active) { [part=menu], [part=trigger], [part=separator] { border-color: ButtonText; } [part=item]:focus { outline-color: Highlight; } }
    </style><button type="button" part="trigger" aria-haspopup="menu"></button><div part="menu" role="menu" tabindex="-1" hidden></div>`;
    this._trigger = this.shadowRoot!.querySelector('[part=trigger]')!;
    this._panel = this.shadowRoot!.querySelector('[part=menu]')!; this._panel.id = generateControlId('menu');
    this._controller = new AnchoredOverlayController(this, this._panel, {
      anchor: () => this._openAnchor || this._trigger,
      anchorRect: () => this._point, placement: () => this.placement, offset: () => this._point ? 0 : 4,
      onDismiss: reason => {
        if (reason === 'anchor-removed' || reason === 'ancestor-closed') { this._restoreOnClose = false; this.open = false; }
        else this.requestOpen(false, reason);
      },
    });
  }
  get label(): string { return this.getAttribute('label')?.trim() || 'Actions'; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get open(): boolean { return isAttrTruthy(this.getAttribute('open')); }
  set open(value: boolean) { reflectBoolean(this, 'open', value); }
  get disabled(): boolean { return isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', value); }
  get controlled(): boolean { return isAttrTruthy(this.getAttribute('controlled')); }
  set controlled(value: boolean) { reflectBoolean(this, 'controlled', value); }
  get placement(): OverlayPlacement { const value = this.getAttribute('placement'); return value === 'bottom-end' || value === 'top-start' || value === 'top-end' ? value : 'bottom-start'; }
  set placement(value: OverlayPlacement) { this.setAttribute('placement', value); }
  get anchor(): HTMLElement | null { return this._anchor || this._byId('for'); }
  set anchor(value: HTMLElement | null) { this._anchor = value instanceof HTMLElement ? value : null; if (this.isConnected) this._bindTargets(); }
  get contextTarget(): HTMLElement | null { return this._contextTarget || this._byId('context-for'); }
  set contextTarget(value: HTMLElement | null) { this._contextTarget = value instanceof HTMLElement ? value : null; if (this.isConnected) this._bindTargets(); }
  get items(): MenuEntry[] { return this._items.map(entry => entry.type === 'group' ? { ...entry, items: entry.items.map(item => ({ ...item })) } : { ...entry }); }
  set items(value: readonly MenuEntry[]) {
    const seen = new Set<string>();
    const item = (value: MenuItem): MenuItem[] => {
      if (!value || typeof value.id !== 'string' || !value.id.trim() || typeof value.label !== 'string' || !value.label.trim() || seen.has(value.id)) return [];
      seen.add(value.id); return [{ id: value.id, label: value.label.trim(), disabled: Boolean(value.disabled) }];
    };
    this._items = (Array.isArray(value) ? value : []).flatMap(entry => {
      if (!entry) return [];
      if (entry.type === 'separator') return [{ type: 'separator' } as MenuEntry];
      if (entry.type === 'group') return typeof entry.label === 'string' && entry.label.trim() && Array.isArray(entry.items) ? [{ type: 'group', label: entry.label.trim(), items: entry.items.flatMap(item) } as MenuEntry] : [];
      return item(entry as MenuItem);
    });
    this._flatItems = this._items.flatMap(entry => entry.type === 'group' ? [...entry.items] : entry.type === 'separator' ? [] : [entry]);
    this._renderItems(); this._sync();
  }
  connectedCallback(): void {
    this._upgrading = true;
    for (const name of ['label', 'items', 'anchor', 'contextTarget', 'open', 'controlled', 'disabled', 'placement']) upgradeProperty(this, name);
    this._upgrading = false;
    this._panel.addEventListener('click', this._select); this._panel.addEventListener('keydown', this._menuKey);
    this._bindTargets(); this._sync();
  }
  disconnectedCallback(): void {
    this._controller.destroy(); this._unbindTargets(); this._panel.removeEventListener('click', this._select); this._panel.removeEventListener('keydown', this._menuKey);
    this._visible = false; this._reflecting = true; this.open = false; this._reflecting = false; this._point = null; this._openAnchor = null;
  }
  attributeChangedCallback(name: string): void {
    if (!this._panel || this._reflecting) return;
    if (this.isConnected && (name === 'for' || name === 'context-for')) this._bindTargets();
    this._sync();
  }
  show(): boolean { this._prepare(this._boundAnchor || this._trigger, null); return this.requestOpen(true, 'api'); }
  hide(): boolean { return this.requestOpen(false, 'api'); }
  showAt(x: number, y: number): boolean {
    const point = Number.isFinite(x) && Number.isFinite(y) ? { left: x, right: x, top: y, bottom: y, width: 0, height: 0 } : null;
    this._prepare(this.contextTarget || this._boundAnchor || this._trigger, point); return this.requestOpen(true, 'context');
  }
  reposition(): void { this._controller.reposition(); }
  override focus(options?: FocusOptions): void { (this._visible ? this._buttons().find(button => button.tabIndex === 0) || this._panel : this._boundAnchor || this._trigger).focus(options); }
  requestOpen(open: boolean, reason = 'api'): boolean {
    if (open && (this.disabled || !this._flatItems.length || !this.isConnected)) return false;
    if (this.open === open) { if (open) { this._sync(); this._focusEdge(); } return true; }
    const restoreBefore = this._restoreOnClose;
    this._restoreOnClose = !['outside', 'anchor-removed', 'ancestor-closed'].includes(reason);
    if (!primitiveEvent(this, 'qds-open-change', { component: this, open, reason }, true)) { this._restoreOnClose = restoreBefore; return false; }
    if (!this.controlled) this.open = open;
    return true;
  }
  private _byId(attribute: string): HTMLElement | null {
    const id = this.getAttribute(attribute), root = this.getRootNode() as Document | ShadowRoot;
    return id ? (root.getElementById?.(id) || this.ownerDocument.getElementById(id)) : null;
  }
  private _buttons(): HTMLButtonElement[] { return [...this._panel.querySelectorAll<HTMLButtonElement>('[data-id]')]; }
  private _renderItems(): void {
    const focused = this.shadowRoot!.activeElement as HTMLElement | null, id = focused?.dataset.id;
    const hadFocus = !!focused && this._panel.contains(focused);
    const make = (entry: MenuItem): HTMLButtonElement => {
      const button = document.createElement('button'); button.type = 'button'; button.dataset.id = entry.id; button.textContent = entry.label;
      button.setAttribute('role', 'menuitem'); button.setAttribute('part', 'item'); button.setAttribute('aria-disabled', String(entry.disabled)); button.tabIndex = -1; return button;
    };
    this._panel.replaceChildren(...this._items.map(entry => {
      if (entry.type === 'separator') { const node = document.createElement('div'); node.setAttribute('role', 'separator'); node.setAttribute('part', 'separator'); return node; }
      if (entry.type === 'group') {
        const group = document.createElement('div'), label = document.createElement('div'); group.setAttribute('role', 'group'); group.setAttribute('part', 'group'); group.setAttribute('aria-label', entry.label);
        label.className = 'group-label'; label.textContent = entry.label; label.setAttribute('aria-hidden', 'true'); group.append(label, ...entry.items.map(make)); return group;
      }
      return make(entry);
    }));
    if (hadFocus && this._flatItems.length) this._focusItem(this._buttons().find(button => button.dataset.id === id) || this._buttons()[0]);
  }
  private _prepare(anchor: HTMLElement, point: QdsMenu['_point']): void {
    if (this._openAnchor !== anchor && this._controller.open) this._controller.hide();
    this._openAnchor = anchor; this._point = point;
  }
  private _focusItem(button: HTMLButtonElement): void {
    for (const item of this._buttons()) item.tabIndex = item === button ? 0 : -1;
    button.focus({ preventScroll: true }); button.scrollIntoView?.({ block: 'nearest' });
  }
  private _focusEdge(): void { const buttons = this._buttons(); if (buttons.length) this._focusItem(this._edge === 'last' ? buttons[buttons.length - 1] : buttons[0]); this._edge = 'first'; }
  private _sync(): void {
    if (this._upgrading) return;
    this._trigger.textContent = this.label + ' ▾'; this._trigger.setAttribute('aria-label', this.label); this._trigger.disabled = this.disabled || !this._flatItems.length;
    this._trigger.hidden = !!this._boundAnchor && this._boundAnchor !== this._trigger;
    this._panel.setAttribute('aria-label', this.getAttribute('menu-label')?.trim() || this.label);
    if (!this.isConnected) return;
    const wasOpen = this._visible;
    if (this.open && !this.disabled && this._flatItems.length) {
      this._openAnchor ||= this._boundAnchor || this._trigger;
      if (!this._controller.show()) { this._reflecting = true; this.open = false; this._reflecting = false; }
      else if (!wasOpen) { this._search = ''; this._focusEdge(); }
    } else {
      const restore = this._restoreOnClose && overlayContains(this._panel, overlayActiveElement(this.ownerDocument));
      this._controller.hide();
      if (this.open) { this._reflecting = true; this.open = false; this._reflecting = false; }
      const anchor = this._openAnchor;
      if (restore) (anchor?.isConnected && (anchor.tabIndex >= 0 || anchor.hasAttribute('tabindex')) ? anchor : this._boundAnchor || this._trigger).focus({ preventScroll: true });
      this._point = null; this._openAnchor = null; this._search = '';
    }
    this._visible = this._controller.open;
    this._boundAnchor?.setAttribute('aria-expanded', String(this._visible));
    this._boundAnchor?.setAttribute('aria-disabled', String(this.disabled || !this._flatItems.length));
    if (wasOpen !== this._visible) primitiveEvent(this, 'qds-toggle', { component: this, open: this._visible });
    this.reposition();
  }
  private _bindTargets(): void {
    const anchor = this.anchor || this._trigger, context = this.contextTarget;
    if (anchor !== this._boundAnchor || context !== this._boundContext) {
      const contextOpen = !!this._openAnchor && this._openAnchor === this._boundContext;
      this._controller.hide(); this._unbindTargets(); this._boundAnchor = anchor; this._boundContext = context;
      this._openAnchor = contextOpen && context ? context : anchor;
      if (contextOpen && !context) this._point = null;
      this._savedAttributes = new Map(['aria-haspopup', 'aria-controls', 'aria-expanded', 'aria-disabled'].map(name => [name, anchor.getAttribute(name)]));
      this._savedControls = 'ariaControlsElements' in anchor && anchor.ariaControlsElements ? [...anchor.ariaControlsElements] : null;
      anchor.setAttribute('aria-haspopup', 'menu'); anchor.setAttribute('aria-controls', this._panel.id);
      if ('ariaControlsElements' in anchor) anchor.ariaControlsElements = [this._panel];
      anchor.addEventListener('click', this._triggerClick); anchor.addEventListener('keydown', this._triggerKey);
      context?.addEventListener('contextmenu', this._contextMenu); context?.addEventListener('keydown', this._contextKey);
    }
    this._sync();
  }
  private _unbindTargets(): void {
    const anchor = this._boundAnchor;
    if (anchor) {
      anchor.removeEventListener('click', this._triggerClick); anchor.removeEventListener('keydown', this._triggerKey);
      if ('ariaControlsElements' in anchor) anchor.ariaControlsElements = null;
      for (const [name, value] of this._savedAttributes) { if (value === null) anchor.removeAttribute(name); else anchor.setAttribute(name, value); }
      if (this._savedControls?.length && !this._savedAttributes.get('aria-controls') && 'ariaControlsElements' in anchor) anchor.ariaControlsElements = this._savedControls;
    }
    this._boundContext?.removeEventListener('contextmenu', this._contextMenu); this._boundContext?.removeEventListener('keydown', this._contextKey);
    this._boundAnchor = null; this._boundContext = null; this._savedAttributes.clear(); this._savedControls = null;
  }
  private _triggerClick = (event: Event): void => { if (event.defaultPrevented) return; event.preventDefault(); this._prepare(this._boundAnchor || this._trigger, null); this.requestOpen(!this.open, 'trigger'); };
  private _triggerKey = (event: KeyboardEvent): void => {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault(); this._edge = event.key === 'ArrowUp' ? 'last' : 'first'; this._prepare(this._boundAnchor || this._trigger, null); this.requestOpen(true, 'trigger');
  };
  private _contextMenu = (event: MouseEvent): void => { if (this.disabled || !this._flatItems.length) return; event.preventDefault(); this.showAt(event.clientX, event.clientY); };
  private _contextKey = (event: KeyboardEvent): void => {
    if (!(event.key === 'ContextMenu' || event.key === 'F10' && event.shiftKey)) return;
    event.preventDefault(); this._prepare(this.contextTarget || this._trigger, null); this.requestOpen(true, 'context');
  };
  private _select = (event: Event): void => {
    const button = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('[data-id]') : null;
    const item = this._flatItems.find(item => item.id === button?.dataset.id);
    if (!item || item.disabled || this.disabled || event.defaultPrevented) return;
    if (primitiveEvent(this, 'qds-select', { component: this, id: item.id, item: { ...item } }, true)) this.requestOpen(false, 'select');
  };
  private _menuKey = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') { this.requestOpen(false, 'tab'); return; }
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    const buttons = this._buttons(); if (!buttons.length) return;
    const current = buttons.indexOf(this.shadowRoot!.activeElement as HTMLButtonElement);
    let target = -1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') target = rovingIndex(current, buttons.length, event.key === 'ArrowDown' ? 1 : -1);
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = buttons.length - 1;
    else if ([...event.key].length === 1 && event.key !== ' ') {
      const now = Date.now(), key = event.key.toLocaleLowerCase();
      this._search = now - this._searchedAt > 600 || this._search === key ? key : this._search + key; this._searchedAt = now;
      for (let step = 1; step <= buttons.length; step++) { const index = rovingIndex(current, buttons.length, step); if (buttons[index].textContent!.toLocaleLowerCase().startsWith(this._search)) { target = index; break; } }
    }
    if (target >= 0) { event.preventDefault(); this._focusItem(buttons[target]); }
  };
}
if (!customElements.get('qds-menu')) customElements.define('qds-menu', QdsMenu);
